import { FC, useRef, useEffect, useCallback, useState } from 'react';

import { Line, unitToPixel } from './types';

/**
 * Экспортирует финальное изображение с рисунком клиента в формате Blob
 * @param backgroundImage URL фонового изображения облака ИЛИ уже загруженный HTMLImageElement
 * @param lines Массив линий, нарисованных клиентом
 * @param width Ширина итогового изображения
 * @param height Высота итогового изображения
 * @returns Promise с Blob изображения
 */
export function exportCloudsImageToBlob(
  backgroundImage: string | HTMLImageElement,
  lines: Line[],
  width: number,
  height: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    // Создаем временный canvas для экспорта
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Не удалось получить контекст canvas'));

      return;
    }

    const drawImage = (img: HTMLImageElement) => {
      try {
        // Проверяем, что изображение загружено
        if (!img.complete || img.naturalWidth === 0) {
          reject(new Error('Изображение еще не загружено'));
          return;
        }

        // Рисуем фоновое изображение на итоговый canvas
        ctx.drawImage(img, 0, 0, width, height);

        // Отдельный слой только для рисования: ластик стирает только линии, не фон.
        // Иначе destination-out даёт прозрачность, и в журнале стёртые места выглядят белыми.
        const drawingCanvas = document.createElement('canvas');
        drawingCanvas.width = width;
        drawingCanvas.height = height;
        const drawingCtx = drawingCanvas.getContext('2d');
        if (!drawingCtx) {
          reject(new Error('Не удалось получить контекст для слоя рисования'));
          return;
        }

        drawingCtx.clearRect(0, 0, width, height);
        drawingCtx.fillStyle = 'rgba(0,0,0,0)';
        drawingCtx.fillRect(0, 0, width, height);

        lines.forEach((line) => {
          const start = unitToPixel(line.x1, line.y1, width, height);
          const end = unitToPixel(line.x2, line.y2, width, height);

          drawingCtx.beginPath();
          drawingCtx.moveTo(start.x, start.y);
          drawingCtx.lineTo(end.x, end.y);

          if (line.tool === 'eraser') {
            drawingCtx.globalCompositeOperation = 'destination-out';
            drawingCtx.strokeStyle = 'rgba(0,0,0,1)';
            drawingCtx.lineWidth = (20 * width) / 800;
          } else {
            drawingCtx.globalCompositeOperation = 'source-over';
            drawingCtx.strokeStyle = line.color || '#000000';

            const scale = width / 800;
            switch (line.tool) {
              case 'pencil':
                drawingCtx.lineWidth = 2 * scale;
                break;
              case 'brush-thin':
                drawingCtx.lineWidth = 3 * scale;
                break;
              case 'brush-medium':
                drawingCtx.lineWidth = 5 * scale;
                break;
              case 'brush-thick':
                drawingCtx.lineWidth = 8 * scale;
                break;
              case 'brush-extra':
                drawingCtx.lineWidth = 12 * scale;
                break;
              default:
                drawingCtx.lineWidth = 3 * scale;
            }
          }

          drawingCtx.lineCap = 'round';
          drawingCtx.lineJoin = 'round';
          drawingCtx.stroke();
          drawingCtx.globalCompositeOperation = 'source-over';
        });

        ctx.globalCompositeOperation = 'source-over';
        ctx.drawImage(drawingCanvas, 0, 0, width, height);
        ctx.globalCompositeOperation = 'source-over';

        // Экспортируем canvas как Blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Не удалось конвертировать canvas в Blob'));
            }
          },
          'image/png',
          1.0,
        );
      } catch (error) {
        reject(error);
      }
    };

    // Если передан уже загруженный Image элемент, используем его напрямую
    if (backgroundImage instanceof HTMLImageElement) {
      drawImage(backgroundImage);
      return;
    }

    // Если передан URL, загружаем изображение
    const img = new Image();
    const timeout = setTimeout(() => {
      reject(new Error('Таймаут загрузки изображения'));
    }, 10000);

    img.onload = () => {
      clearTimeout(timeout);
      try {
        // Проверяем, что изображение загружено корректно
        if (img.width === 0 || img.height === 0) {
          reject(new Error('Изображение имеет нулевой размер'));
          return;
        }
        drawImage(img);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      clearTimeout(timeout);
      reject(new Error('Не удалось загрузить фоновое изображение'));
    };

    img.src = backgroundImage;
  });
}

interface CloudsViewerProps {
  backgroundImage: string;
  lines: Line[];
  className?: string;
}

/**
 * Компонент для просмотра рисунка клиента психологом (read-only)
 */
export const CloudsViewer: FC<CloudsViewerProps> = ({ backgroundImage, lines, className = '' }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingCanvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [isLoadingBackgroundImage, setIsLoadingBackgroundImage] = useState(false);

  // Загрузка фонового изображения
  useEffect(() => {
    if (!backgroundImage) return;

    setIsLoadingBackgroundImage(true);
    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      setIsLoadingBackgroundImage(false);
    };
    img.onerror = () => {
      console.error('Failed to load cloud image');
      setIsLoadingBackgroundImage(false);
    };
    img.src = backgroundImage;
  }, [backgroundImage]);

  // Перерисовка canvas
  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const drawingCanvas = drawingCanvasRef.current;
    if (!canvas || !drawingCanvas) return;

    const ctx = canvas.getContext('2d');
    const drawingCtx = drawingCanvas.getContext('2d');
    if (!ctx || !drawingCtx) return;

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();

    // Устанавливаем размеры для обоих canvas
    canvas.width = rect.width;
    canvas.height = rect.height;
    drawingCanvas.width = rect.width;
    drawingCanvas.height = rect.height;

    // Очищаем фоновый canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Рисуем фоновое изображение только на фоновом canvas
    if (imageRef.current) {
      ctx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height);
    }

    // Очищаем canvas для рисования
    drawingCtx.clearRect(0, 0, drawingCanvas.width, drawingCanvas.height);

    // Рисуем все линии на canvas для рисования (полный redraw — надежнее для data-channel)
    lines.forEach((line) => {
      const start = unitToPixel(line.x1, line.y1, drawingCanvas.width, drawingCanvas.height);
      const end = unitToPixel(line.x2, line.y2, drawingCanvas.width, drawingCanvas.height);

      drawingCtx.beginPath();
      drawingCtx.moveTo(start.x, start.y);
      drawingCtx.lineTo(end.x, end.y);

      if (line.tool === 'eraser') {
        drawingCtx.globalCompositeOperation = 'destination-out';
        drawingCtx.strokeStyle = 'rgba(0,0,0,1)';
        drawingCtx.lineWidth = 20;
      } else {
        drawingCtx.globalCompositeOperation = 'source-over';
        drawingCtx.strokeStyle = line.color || '#000000';

        switch (line.tool) {
          case 'pencil':
            drawingCtx.lineWidth = 2;
            break;
          case 'brush-thin':
            drawingCtx.lineWidth = 3;
            break;
          case 'brush-medium':
            drawingCtx.lineWidth = 5;
            break;
          case 'brush-thick':
            drawingCtx.lineWidth = 8;
            break;
          case 'brush-extra':
            drawingCtx.lineWidth = 12;
            break;
          default:
            drawingCtx.lineWidth = 3;
        }
      }

      drawingCtx.lineCap = 'round';
      drawingCtx.lineJoin = 'round';
      drawingCtx.stroke();
      drawingCtx.globalCompositeOperation = 'source-over';
    });
  }, [lines]);

  // Перерисовка canvas после скрытия лоадера
  useEffect(() => {
    if (!isLoadingBackgroundImage && imageRef.current) {
      // Используем requestAnimationFrame для отложенной перерисовки после рендеринга DOM
      requestAnimationFrame(() => {
        const container = containerRef.current;
        const canvas = canvasRef.current;
        const drawingCanvas = drawingCanvasRef.current;

        if (container && canvas && drawingCanvas) {
          const rect = container.getBoundingClientRect();
          // Проверяем, что контейнер имеет размеры
          if (rect.width > 0 && rect.height > 0) {
            redrawCanvas();
          } else {
            // Если размеры еще не установлены, пробуем еще раз через небольшую задержку
            setTimeout(() => {
              redrawCanvas();
            }, 50);
          }
        }
      });
    }
  }, [isLoadingBackgroundImage, redrawCanvas]);

  // Перерисовка при изменении линий
  useEffect(() => {
    if (!isLoadingBackgroundImage && imageRef.current) {
      redrawCanvas();
    }
  }, [lines, isLoadingBackgroundImage, redrawCanvas]);

  // Обработка изменения размера окна
  useEffect(() => {
    const handleResize = () => redrawCanvas();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [redrawCanvas]);

  return (
    <div ref={containerRef} className={`relative w-full h-full ${className}`}>
      {isLoadingBackgroundImage ? (
        <div className="absolute inset-0 flex items-center justify-center bg-white rounded-lg">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Фоновый canvas с изображением */}
          <canvas ref={canvasRef} className="absolute top-0 left-0 w-full h-full rounded-lg pointer-events-none" />
          {/* Canvas для рисования поверх фона */}
          <canvas
            ref={drawingCanvasRef}
            className="absolute top-0 left-0 w-full h-full rounded-lg pointer-events-none"
          />
        </>
      )}
    </div>
  );
};
